import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';

export const LAZY_COMPONENT_QUERY_KEY = 'lazy-component';

const LAZY_QUERY_OPTIONS = {
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export function useLazyComponent(key, importFn) {
  const { data: Component, isFetching } = useQuery({
    queryKey: [LAZY_COMPONENT_QUERY_KEY, key],
    queryFn: async () => {
      const mod = await importFn();
      return mod.default;
    },
    enabled: !!importFn,
    ...LAZY_QUERY_OPTIONS,
  });

  return { Component: Component ?? null, isLoading: !!importFn && isFetching };
}

export function usePreloadBlockTemplates(templateBlockIds, blockTemplates, enabled) {
  const queryKey = useMemo(() => [LAZY_COMPONENT_QUERY_KEY, 'preload', ...templateBlockIds], [templateBlockIds]);

  const { data: components, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      Promise.all(
        templateBlockIds.map(async (id) => {
          const config = blockTemplates[id];
          if (!config?.importFn) return [id, null];
          const mod = await config.importFn();
          return [id, mod.default];
        }),
      ).then(Object.fromEntries),
    enabled: !!enabled && !isEmpty(templateBlockIds),
    ...LAZY_QUERY_OPTIONS,
  });

  return { components: components ?? null, isFetching };
}
